import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Wrench, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import CitySelector from "@/components/CitySelector";
import { useAuth } from "@/context/AuthContext";

const COMPONENTS = [
  "Engine oil", "Oil filter", "Air filter", "Fuel filter",
  "Spark plugs", "Coolant", "Brake pads", "Brake fluid",
  "Tyres", "Wiper blades", "Battery", "AC service",
  "Wheel alignment", "General inspection",
];

/**
 * LogService — structured service entry.
 * Item 7: Log Service must be a form (not a camera flow).
 */
export default function LogService() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [city, setCity] = useState(user?.preferred_city || "");
  const [totalDirty, setTotalDirty] = useState(false);
  const [form, setForm] = useState({
    vehicle_id: "",
    odometer: "",
    date: new Date().toISOString().slice(0, 16),
    workshop: "",
    components: [],
    labour_amount: "",
    parts_amount: "",
    total_amount: "",
    warranty_months: "",
    next_service_due_km: "",
    next_service_due_date: "",
    notes: "",
  });

  useEffect(() => {
    api.get("/vehicles").then((r) => {
      // Show only vehicles the user can act on
      const assigned = user?.assigned_vehicle_ids || [];
      const all = r.data || [];
      const list = (user?.is_admin || assigned.length === 0)
        ? all
        : all.filter((v) => assigned.includes(v.id));
      setVehicles(list);
    });
  }, [user]);

  const toggleComponent = (c) => {
    setForm((f) => ({
      ...f,
      components: f.components.includes(c)
        ? f.components.filter((x) => x !== c)
        : [...f.components, c],
    }));
  };

  const derivedTotal = useMemo(() => {
    const l = parseFloat(form.labour_amount) || 0;
    const p = parseFloat(form.parts_amount) || 0;
    return l + p;
  }, [form.labour_amount, form.parts_amount]);

  // Auto-fill the Total from labour + parts as long as the user hasn't manually edited the total field yet.
  useEffect(() => {
    if (!totalDirty) {
      setForm((f) => ({ ...f, total_amount: derivedTotal > 0 ? String(derivedTotal.toFixed(2)) : "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [derivedTotal, totalDirty]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vehicle_id) return toast.error("Select a vehicle");
    if (!form.odometer) return toast.error("Enter the odometer reading");
    setSaving(true);
    try {
      const payload = {
        vehicle_id: form.vehicle_id,
        kind: "routine",
        odometer: parseFloat(form.odometer),
        title: form.components.length ? form.components.join(", ") : "Routine service",
        date: form.date,
        workshop: form.workshop || null,
        components: form.components,
        labour_amount: form.labour_amount ? parseFloat(form.labour_amount) : null,
        parts_amount:  form.parts_amount  ? parseFloat(form.parts_amount)  : null,
        total_amount:  form.total_amount  ? parseFloat(form.total_amount)  : (derivedTotal || null),
        warranty_months:      form.warranty_months      ? parseInt(form.warranty_months, 10) : null,
        next_service_due_km:  form.next_service_due_km  ? parseFloat(form.next_service_due_km) : null,
        next_service_due_date: form.next_service_due_date || null,
        notes: form.notes || null,
      };
      await api.post("/service-logs", payload);
      toast.success("Service logged");
      nav("/", { replace: true });
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="max-w-2xl mx-auto space-y-6" data-testid="log-service-page">
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => nav(-1)}
          className="w-9 h-9 border border-[var(--border-line)] hover:bg-[var(--hover)] flex items-center justify-center rounded-lg" aria-label="Back">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <div className="ft-overline text-xs">SERVICE</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter flex items-center gap-2">
            <Wrench size={26} className="text-[#FBBF24]" /> Log routine service
          </h1>
        </div>
        <CitySelector value={city} onChange={setCity} compact testid="log-service-city" />
      </div>

      <div className="rounded-xl border border-[var(--border-line)] bg-[var(--bg-surface)] p-5 space-y-4">
        <Field label="Vehicle" required>
          <select
            value={form.vehicle_id}
            onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })}
            className="input" data-testid="log-service-vehicle" required
          >
            <option value="">Select vehicle</option>
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>{v.number} · {v.name}</option>
            ))}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Odometer (km)" required>
            <input type="number" step="1" required className="input" value={form.odometer}
              onChange={(e) => setForm({ ...form, odometer: e.target.value })} data-testid="log-service-odometer" />
          </Field>
          <Field label="Date">
            <input type="datetime-local" className="input" value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="log-service-date" />
          </Field>
        </div>
        <Field label="Workshop / Vendor">
          <input type="text" className="input" value={form.workshop}
            onChange={(e) => setForm({ ...form, workshop: e.target.value })} data-testid="log-service-workshop" />
        </Field>
      </div>

      <div className="rounded-xl border border-[var(--border-line)] bg-[var(--bg-surface)] p-5">
        <div className="ft-overline mb-2">COMPONENTS SERVICED</div>
        <div className="flex flex-wrap gap-2">
          {COMPONENTS.map((c) => (
            <button type="button" key={c} onClick={() => toggleComponent(c)}
              className={"px-3 py-1.5 rounded-full border text-xs " +
                (form.components.includes(c)
                  ? "border-[#FF5A1F] text-[#FF5A1F] bg-[#FF5A1F]/10"
                  : "border-[var(--border-line)] text-[var(--text-muted)] hover:bg-[var(--hover)]")}
              data-testid={`comp-${c.toLowerCase().replace(/\s+/g,'-')}`}>
              {c}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-[var(--border-line)] bg-[var(--bg-surface)] p-5 space-y-3">
        <div className="ft-overline">CHARGES</div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Labour ₹">
            <input type="number" step="0.01" className="input" value={form.labour_amount}
              onChange={(e) => setForm({ ...form, labour_amount: e.target.value })} data-testid="log-service-labour" />
          </Field>
          <Field label="Parts ₹">
            <input type="number" step="0.01" className="input" value={form.parts_amount}
              onChange={(e) => setForm({ ...form, parts_amount: e.target.value })} data-testid="log-service-parts" />
          </Field>
          <Field label={`Total ₹ ${derivedTotal ? `(auto ${derivedTotal.toFixed(0)})` : ""}`}>
            <input type="number" step="0.01" className="input" value={form.total_amount}
              placeholder={derivedTotal ? String(derivedTotal.toFixed(2)) : ""}
              onChange={(e) => { setTotalDirty(true); setForm({ ...form, total_amount: e.target.value }); }} data-testid="log-service-total" />
          </Field>
        </div>
      </div>

      <div className="rounded-xl border border-[var(--border-line)] bg-[var(--bg-surface)] p-5 space-y-3">
        <div className="ft-overline">NEXT SERVICE / WARRANTY</div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Warranty (months)">
            <input type="number" step="1" className="input" value={form.warranty_months}
              onChange={(e) => setForm({ ...form, warranty_months: e.target.value })} data-testid="log-service-warranty" />
          </Field>
          <Field label="Next due (km)">
            <input type="number" step="1" className="input" value={form.next_service_due_km}
              onChange={(e) => setForm({ ...form, next_service_due_km: e.target.value })} data-testid="log-service-next-km" />
          </Field>
          <Field label="Next due (date)">
            <input type="date" className="input" value={form.next_service_due_date}
              onChange={(e) => setForm({ ...form, next_service_due_date: e.target.value })} data-testid="log-service-next-date" />
          </Field>
        </div>
        <Field label="Notes">
          <textarea className="input min-h-[70px]" value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="log-service-notes" />
        </Field>
      </div>

      <button
        type="submit" disabled={saving}
        className="w-full bg-[#FF5A1F] hover:bg-[#E64A0F] text-white font-bold py-3 rounded-lg inline-flex items-center justify-center gap-2"
        data-testid="log-service-submit">
        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save service
      </button>
    </form>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="ft-overline block mb-1">{label}{required && <span className="text-[#FF5A1F]"> *</span>}</span>
      {children}
    </label>
  );
}
